import { createToken, Lexer, EmbeddedActionsParser } from 'chevrotain'
import { defaultFuncList, IFormulaFunc, IInstruction } from './basic'

export class FormulaParser extends EmbeddedActionsParser {
  funcMap = new Map<string, IFormulaFunc>()
  lexer = new Lexer(allTokens)
  rules: {
    FORMULA: any
    FUNC: any
    VALUE: any
    VALUES: any
  } = {
    // A value prefaced by an equals sign is a valid formula
    FORMULA: this.RULE('formula', () => {
      this.CONSUME1(Equals)
      return this.SUBRULE(this.rules.VALUE)
    }),

    // A func is a token of form NAME(value1, value) that can be executed
    FUNC: this.RULE('functions', () => {
      const nameToken = this.CONSUME(Identifier)
      this.CONSUME(LBracket)
      const args: any = this.SUBRULE(this.rules.VALUES)
      this.CONSUME(RBracket)

      return {
        type: 'func',
        name: nameToken.image,
        params: args,
      } as IInstruction
    }),

    // A value is an individual token like a string, or a func
    VALUE: this.RULE('value', () => {
      return this.OR([
        { ALT: () => this.SUBRULE(this.rules.FUNC) },
        { ALT: () => {
          const str = this.CONSUME(StringLiteral)
          return str.image
        } },
        { ALT: () => {
          const str = this.CONSUME(Integer)
          return str.image
        } },
      ])
    }),

    // Values is a list of value tokens, seperated by commas
    VALUES: this.RULE('values', () => {
      const result: any[] = []
      this.MANY_SEP({
        SEP: Comma,
        DEF: () => {
          const val = this.SUBRULE(this.rules.VALUE)
          result.push(val)
        },
      })
      return result
    }),
  }

  constructor(public funcs: IFormulaFunc[] = defaultFuncList) {
    super(allTokens)
    funcs.forEach(f => this.funcMap.set(f.key, f))
    this.performSelfAnalysis()
  }

  async calculate(formulaString: string): Promise<any> {
    const tokenized = this.lexer.tokenize(formulaString)
    if (tokenized.errors && tokenized.errors.length) { throw new Error('Invalid Syntax') }
    this.input = tokenized.tokens
    const instruction: IInstruction = (this as any)['formula']()
    return this.execute(instruction)
  }

  execute(i: IInstruction) {
    if (!this.isInstruction(i)) { return i }

    switch (i.type) {
      case 'func':
        const func = this.funcMap.get(i.name)
        if (!func) { throw new Error(`no func named "${i.name}'`) }
        const params: any[] = i.params.map((p) => this.execute(p))
        return func.perform(...params)
    }
  }

  protected isInstruction(i: any) {
    return typeof i === 'object' && i['type'] !== undefined
  }
}

const Equals = createToken({ name: 'equals', pattern: /=/ })
const LBracket = createToken({ name: 'LBracket', pattern: /\(/ })
const RBracket = createToken({ name: 'RBracket', pattern: /\)/ })
const Comma = createToken({ name: 'Comma', pattern: /,/ })
const Identifier = createToken({ name: 'identifier', pattern: /[A-z]\w*/ })
const Integer = createToken({ name: 'Integer', pattern: /0|[1-9]\d*/ })
const WhiteSpace = createToken({ name: 'WhiteSpace', pattern: /\s+/, group: Lexer.SKIPPED })
const StringLiteral = createToken({ name: 'StringLiteral', pattern: /"(:?[^\\"\n\r]+|\\(:?[bfnrtv"\\/]|u[0-9a-fA-F]{4}))*"/ })

const allTokens = [
  WhiteSpace, // note we are placing WhiteSpace first as it is very common thus it will speed up the lexer.
  Equals,
  StringLiteral,
  LBracket,
  RBracket,
  Identifier,
  Integer,
  Comma,
]
